import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import type { SearchResult } from '@fint/validation';
import { useSwitchOrg } from '@/hooks/useSwitchOrg';
import { useAuthStore } from '@/stores/auth';
import { PageHeader } from '@/app/DashboardLayout';
import { Card } from '@/features/common/ui';
import { SearchResultsList } from './SearchResultsList';
import { useGlobalSearch } from './useGlobalSearch';

/**
 * Every match for a search - where Enter in the search bar goes, the way a
 * search engine's results page follows its suggestions. Up to fifty of each
 * kind per book rather than the bar's six, in the same groups, and the query
 * is in the URL so a search can be bookmarked or sent.
 */
export function SearchPage() {
  const [params, setParams] = useSearchParams();
  const initial = params.get('q') ?? '';
  const navigate = useNavigate();
  const switchOrg = useSwitchOrg();
  const activeOrgId = useAuthStore((st) => st.activeOrgId);
  const s = useGlobalSearch(activeOrgId, { limit: 50, initialQ: initial });

  // The bar sends a new ?q= while this page is open: follow it.
  useEffect(() => {
    if (initial !== s.q) s.setQ(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial]);

  const pick = (r: SearchResult) => {
    if (r.orgId !== activeOrgId) switchOrg(r.orgId, r.href);
    else navigate(r.href);
  };
  const total = s.flat.length;

  return (
    <>
      <PageHeader title="Search" subtitle={s.debounced.length >= 2 ? `${total} result${total === 1 ? '' : 's'} for “${s.debounced}”` : 'Search the whole book'} />
      <div className="space-y-4 px-5 pb-8">
        <form
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            setParams(s.q.trim() ? { q: s.q.trim() } : {});
          }}
          className="flex max-w-2xl items-center gap-2 rounded-full border border-input bg-background px-4 shadow-sm focus-within:border-primary focus-within:ring-2 focus-within:ring-ring/30"
        >
          <Search className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          <input
            type="search"
            value={s.q}
            onChange={(e) => s.setQ(e.target.value)}
            onBlur={() => setParams(s.q.trim() ? { q: s.q.trim() } : {}, { replace: true })}
            aria-label="Search"
            data-testid="search-page-input"
            className="h-11 min-w-0 flex-1 bg-transparent text-base outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
          />
        </form>
        <Card className="overflow-hidden" data-testid="search-page-results">
          <SearchResultsList
            groups={s.groups}
            flat={s.flat}
            highlight={-1}
            onPick={pick}
            query={s.q}
            debounced={s.debounced}
            isFetching={s.isFetching}
            manyBooks={s.manyBooks}
            amount={s.data?.amount ? Number(s.data.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 }) : null}
            dense={false}
          />
        </Card>
      </div>
    </>
  );
}
